// modules/services/InKindServiceCreate.js
import React, { useState, useEffect } from 'react';
import {
  AutocompleteInput,
  Create,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  usePermissions,
  useNotify,
  useRedirect,
  useCreate,
  useInput,
} from "react-admin";
import { Box, Card, CardContent, Typography, Chip, Alert, Divider, Stack } from '@mui/material';
import { Build as ServiceIcon, VolunteerActivism as DonateIcon, Help as NeedIcon } from '@mui/icons-material';
import { useEffect as useEffectReact, useRef } from "react";

const token = JSON.parse(localStorage.getItem('token'));

// Custom User Input Component (reused from your existing component)
const UserPhoneInput = (props) => {
  const { source, validate } = props;
  const { field, fieldState } = useInput({ source, validate });
  const notify = useNotify();

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef();

  const searchUsers = async (searchText) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/admin/users?search=${encodeURIComponent(searchText)}&page=1&limit=25`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  };

  useEffectReact(() => {
    if (inputValue.length < 3) {
      setOptions([]);
      setShowDropdown(false);
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        console.log('Searching for users with:', inputValue);
        const users = await searchUsers(inputValue);
        console.log('Search results:', users);
        setOptions(users);
        setShowDropdown(users.length > 0);
      } catch (error) {
        console.error('Error searching users:', error);
        notify('Error searching users', { type: 'error' });
        setOptions([]);
        setShowDropdown(false);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [inputValue, notify]);

  // Load initial value if editing
  useEffectReact(() => {
    if (field.value && typeof field.value === 'string') {
      fetch(`${process.env.REACT_APP_URL}/admin/users/${field.value}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })
        .then(response => response.json())
        .then(user => {
          setOptions([user]);
          setSelectedUser(user);
          setInputValue(`${user.phonenumber}${user.name ? ` - ${user.name}` : ''}`);
        })
        .catch(error => {
          console.error('Error fetching user:', error);
        });
    }
  }, [field.value]);

  const handleInputChange = (event) => {
    const newInputValue = event.target.value;
    setInputValue(newInputValue);

    if (newInputValue !== selectedUser?.phonenumber) {
      setSelectedUser(null);
      field.onChange('');
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setInputValue(`${user.phonenumber}${user.name ? ` - ${user.name}` : ''}`);
    field.onChange(user.id);
    setShowDropdown(false);
  };

  const handleInputFocus = () => {
    if (options.length > 0 && inputValue.length >= 3) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowDropdown(false), 150);
  };

  const handleDropdownMouseDown = (event) => {
    event.preventDefault();
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    setInputValue('');
    field.onChange('');
    setOptions([]);
  };

  return (
    <div className="space-y-2 relative">
      <label className="block text-sm font-medium text-gray-700">User Phone *</label>

      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder="Type at least 3 characters to search users by phone number..."
          className={`bg-white w-full px-3 py-2 border rounded-md shadow-sm ${fieldState.error ? 'border-red-500' : 'border-gray-300'
            } ${selectedUser ? 'bg-green-50' : ''}`}
          disabled={loading}
        />

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          </div>
        )}

        {selectedUser && !loading && (
          <button
            type="button"
            onClick={handleClearSelection}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        )}
      </div>

      {showDropdown && options.length > 0 && (
        <div
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          onMouseDown={handleDropdownMouseDown}
        >
          {options.map((user) => (
            <div
              key={user.id}
              onClick={() => handleUserSelect(user)}
              className="px-4 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors duration-150"
            >
              <div className="font-medium text-gray-900">{user.phonenumber}</div>
              {user.name && (
                <div className="text-sm text-gray-600">{user.name}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {showDropdown && inputValue.length >= 3 && options.length === 0 && !loading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="px-4 py-2 text-gray-500 text-sm">
            No users found for "{inputValue}"
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="p-2 bg-green-50 border border-green-200 rounded-md">
          <p className="text-xs text-green-700">
            ✓ Selected: <span className="font-semibold">{selectedUser.phonenumber}</span>
            {selectedUser.name && ` - ${selectedUser.name}`}
          </p>
        </div>
      )}

      {fieldState.error && (
        <p className="text-sm text-red-600">{fieldState.error.message}</p>
      )}

      <p className="text-xs text-gray-500">
        Start typing a phone number (minimum 3 characters) to search users
      </p>
    </div>
  );
};

// Main InKindServiceCreate Component
export const InKindServiceCreate = (props) => {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create] = useCreate();
  const [serviceType, setServiceType] = useState('supply');

  const transform = (data) => {
    console.log('Original form data:', data);

    const transformed = {
      owner: data.owner,
      service_id: Number(data.service_id),
      description: data.description,
      // For in-kind services, price_per_day is not applicable
      price_per_day: undefined,
      type: data.type || undefined,
      quantity: data.quantity || undefined,
      payload: data.payload || undefined,
      line: data.line || undefined,
      temperature: data.temperature || undefined,
      height: data.height || undefined,
      space: data.space || undefined,
      number_of_workers: data.number_of_workers || undefined,
      state: data.state ? Number(data.state) : undefined,
      city: data.city ? Number(data.city) : undefined,
      locality: data.locality ? Number(data.locality) : undefined,
      vehicle_type_id: data.vehicle_type_id ? Number(data.vehicle_type_id) : undefined,
      service_type: data.service_type || 'supply',
      status: data.status || 'active',
      // Add in-kind specific fields
      is_in_kind: true, // Mark as in-kind service
      in_kind_notes: data.in_kind_notes || undefined,
    };

    console.log('Transformed in-kind service data:', transformed);
    return transformed;
  };

  const handleSubmit = async (data) => {
    try {
      const transformedData = transform(data);

      // Create the in-kind service using the same services endpoint
      await create('services', {
        data: transformedData,
      });

      notify('In-kind service created successfully!', { type: 'success' });
      const dist = transformedData.service_type === 'donate' ? 'thabat-offers' : 'thabat-requests';
      redirect(`/reports/${dist}`); // Or wherever you want to redirect
    } catch (error) {
      console.error('Error creating in-kind service:', error);
      notify(error.message || 'Failed to create in-kind service', { type: 'error' });
    }
  };

  const canEdit = permissions === "admin" || permissions === "operater";

  if (!canEdit) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to create in-kind services.
        </Alert>
      </Box>
    );
  }

  return (
    <Create {...props} title="Create In-Kind Service" mutationOptions={{}}>
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <ServiceIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Create In-Kind Service
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a donation or request for in-kind services (free or volunteer-based)
                </Typography>
              </Box>
              <Chip
                label="In-Kind Service"
                color="primary"
                variant="outlined"
                icon={<DonateIcon />}
              />
            </Stack>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>In-Kind Services</strong> are non-monetary service contributions or requests.
                Supply represents services you want to offer for free, while Demand represents services you're looking for.
              </Typography>
            </Alert>

            <SimpleForm
              onSubmit={handleSubmit}
              toolbar={null}
              className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md"
            >
              {/* Header Section */}
              <div className="mb-8">
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Divider />
              </div>

              {/* Row 1: User and Service Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <UserPhoneInput source="owner" validate={[required()]} />

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Service Category *</label>
                  <ReferenceInput source="service_id" reference="servicetypes" validate={[required()]}>
                    <SelectInput
                      optionText="name"
                      optionValue="id"
                      label={false}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </ReferenceInput>
                </div>
              </div>

              {/* Row 2: Service Type (Supply/Demand) and Description */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Service Direction *</label>
                  <SelectInput
                    source="service_type"
                    choices={[
                      { id: "donate", name: "Supply (I want to offer this service for free)" },
                      { id: "need", name: "Demand (I need this service for free)" },
                    ]}
                    defaultValue="donate"
                    onChange={(value) => setServiceType(value)}
                    label={false}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    validate={[required()]}
                  />
                </div>

                <div className="md:col-span-3 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {serviceType === 'donate' ? 'What service are you offering? *' : 'What service do you need? *'}
                  </label>
                  <TextInput
                    multiline
                    rows={2}
                    source="description"
                    validate={[required()]}
                    label={false}
                    fullWidth
                    placeholder={
                      serviceType === 'supply'
                        ? 'Describe the service you want to offer for free (e.g., "Free transportation of goods within Khartoum")'
                        : 'Describe the service you need (e.g., "Looking for free storage space for agricultural products")'
                    }
                    className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* In-Kind Service Specific Section */}
              <div className="mb-8">
                <Typography variant="h6" gutterBottom>
                  Service Details
                </Typography>
                <Divider />

                <div className="mt-4 space-y-4">
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      This is an <strong>in-kind service</strong> - no monetary compensation will be involved.
                      {serviceType === 'supply' 
                        ? ' You are offering this service for free as a donation.' 
                        : ' You are requesting this service to be provided for free.'}
                    </Typography>
                  </Alert>

                  {/* Service Specifications Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Service Type Details</label>
                      <TextInput
                        source="type"
                        label={false}
                        fullWidth
                        placeholder="e.g., Loading, Transport, Storage, Cleaning"
                        className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Quantity/Number of Services</label>
                      <TextInput
                        source="quantity"
                        label={false}
                        fullWidth
                        placeholder="e.g., 5 trips, 10 hours, 3 days"
                        className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Number of Workers</label>
                      <TextInput
                        source="number_of_workers"
                        label={false}
                        fullWidth
                        placeholder="Number of people needed/available"
                        className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Transport/Vehicle Specific Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Vehicle Type (If applicable)</label>
                      <ReferenceInput source="vehicle_type_id" reference="vehicle-types">
                        <SelectInput
                          optionText="name"
                          optionValue="id"
                          label={false}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </ReferenceInput>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Payload Capacity</label>
                      <TextInput
                        source="payload"
                        label={false}
                        fullWidth
                        placeholder="e.g., 5 tons, 1000kg"
                        className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Storage Space</label>
                      <TextInput
                        source="space"
                        label={false}
                        fullWidth
                        placeholder="e.g., 100 sqm, 500 cubic feet"
                        className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Additional Specifications */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Route/Line</label>
                      <TextInput
                        source="line"
                        label={false}
                        fullWidth
                        placeholder="e.g., Khartoum to Port Sudan"
                        className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Temperature Control</label>
                      <TextInput
                        source="temperature"
                        label={false}
                        fullWidth
                        placeholder="e.g., Cold storage, Room temperature"
                        className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Height/Size Requirements</label>
                      <TextInput
                        source="height"
                        label={false}
                        fullWidth
                        placeholder="e.g., Max height 2m, Oversized load"
                        className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div className="space-y-2 mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Additional Notes for In-Kind Service
                    </label>
                    <TextInput
                      multiline
                      rows={3}
                      source="in_kind_notes"
                      label={false}
                      fullWidth
                      placeholder="Special instructions, conditions, or notes about this in-kind service (e.g., availability times, special requirements, etc.)"
                      className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Location Section */}
              <div className="mb-8">
                <Typography variant="h6" gutterBottom>
                  Location Information
                </Typography>
                <Divider />

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <ReferenceInput source="state" reference="state">
                      <SelectInput
                        optionText="name"
                        optionValue="id"
                        label={false}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </ReferenceInput>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <ReferenceInput source="city" reference="city">
                      <SelectInput
                        optionText="name"
                        optionValue="id"
                        label={false}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </ReferenceInput>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Locality</label>
                    <ReferenceInput source="locality" reference="locality">
                      <SelectInput
                        optionText="name"
                        optionValue="id"
                        label={false}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </ReferenceInput>
                  </div>
                </div>
              </div>

              {/* Status Section */}
              <div className="mb-8">
                <Typography variant="h6" gutterBottom>
                  Service Status
                </Typography>
                <Divider />

                <div className="mt-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Status *</label>
                    <SelectInput
                      source="status"
                      choices={[
                        { id: "active", name: "Active" },
                        { id: "pending", name: "Pending" },
                        { id: "inactive", name: "Inactive" },
                      ]}
                      defaultValue="active"
                      label={false}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      validate={[required()]}
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                <Typography variant="body2" color="text.secondary">
                  This will create an <strong>in-kind service</strong> {serviceType === 'supply' ? 'donation' : 'request'} that will appear in the in-kind services dashboard.
                </Typography>
                <SaveButton
                  label={serviceType === 'supply' ? 'Create Free Service Offer' : 'Create Service Request'}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-md shadow-sm transition duration-200"
                />
              </div>
            </SimpleForm>
          </CardContent>
        </Card>
      </Box>
    </Create>
  );
};

export default InKindServiceCreate;